



"use client";

import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Sun, Moon, Menu, X, ChevronDown } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { useTheme } from "@/app/context/ThemeContext";
import { useRouter } from "next/navigation"; // Next.js 13+ App Router
 
interface LogoData {
  namelogo: string;
  url: string | null;
  Reference: string;
}

interface Specialty {
  id: number;
  label: string;
  url: string;
  order: number;
  locale: string;
}

interface NavLink {
  id: number;
  label: string;
  url: string;
  submenu?: Specialty[] | any[];
}

export default function Navbar() {
  const [logo, setLogo] = useState<LogoData | null>(null);
  const [specialties, setSpecialties] = useState<Specialty[]>([]);
  const [navLinks, setNavLinks] = useState<NavLink[]>([]);
  const [locateUs, setLocateUs] = useState<NavLink | null>(null);

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<number | null>(null);
const router = useRouter();
  const firstRender = useRef(true);
   const { language, setLanguage } = useLanguage();
  const { theme, toggleTheme } = useTheme();

 

  // ---------------------------- RTL/LTR ----------------------------
  useEffect(() => {
    document.documentElement.dir = language === "ar" ? "rtl" : "ltr";
 
  }, [language]);

 /*
  useEffect(() => {
    const fetchData = async () => {
      try {
        // ---------------------------- LOGO FETCH ----------------------------
        const logoRes = await fetch(
          `${process.env.NEXT_PUBLIC_STRAPI_URL}/api/logos?populate=*&locale=${language}`
        );
        const logoJson = await logoRes.json();

        if (logoJson.data?.length > 0) {
          const item = logoJson.data[0];
          const logoArray = item.logo || [];
          const imageUrl =
            logoArray.length > 0
              ? `${process.env.NEXT_PUBLIC_STRAPI_URL}${logoArray[0].url}`
              : null;

          setLogo({
            namelogo: item.namelogo || "ENAP",
            Reference: item.Reference,
            url: imageUrl,
          });
        }

        // ---------------------------- FETCH SPECIALTIES ----------------------------
        const specRes = await fetch(
          `${process.env.NEXT_PUBLIC_STRAPI_URL}/api/categoryspecialitesses?populate=*&locale=${language}`
        );
        const specJson = await specRes.json();

        // Créer le tableau avec id, label, url et order
        const specialtiesData = specJson.data.map((item: any) => ({
          id: item.id,
          label: item.Name,
          url: `/Specialties/${item.Slug}`,
          order: item.Order || 0, // ajouter la variable Order
        }));

        // Trier par ordre
        //specialtiesData.sort((a, b) => a.order - b.order);
        specialtiesData.sort((a: Specialty, b: Specialty) => a.order - b.order);


        // Ensuite mettre dans le state
        setSpecialties(specialtiesData);


        // ---------------------------- NAVBAR LINKS ----------------------------
        const linksRes = await fetch(
          `${process.env.NEXT_PUBLIC_STRAPI_URL}/api/navbar-links?populate=*&locale=${language}&sort=Order:asc`
        );
        const linksJson = await linksRes.json();

        let linksData = linksJson.data.map((item: any) => {
          const label = item.attributes?.label ?? item.label;

          return {
            id: item.id,
            label,
            url: item.attributes?.url ?? item.url,
            submenu:
              label.toLowerCase() === "specialties" ||
                label.toLowerCase() === "spécialités" ||
                label.toLowerCase() === "التخصصات"
                ? specialtiesData
                : item.attributes?.submenu || [],
          };
        });

        // Last item = locateUs button
        setLocateUs(linksData[linksData.length - 1]);
        setNavLinks(linksData.slice(0, -1));
      } catch (err) {
        console.error(err);
      }
    };

    fetchData();
  }, [language]);
*/
useEffect(() => {
  const fetchData = async () => {
    try {
      const res = await fetch(`/api/navbar?locale=${language}`);
      if (!res.ok) throw new Error("Fetch failed");
      const json = await res.json();

      // Logo
      setLogo(json.logo ? {
        namelogo: json.logo.namelogo,
        Reference: json.logo.Reference,
        url: json.logo.logo?.[0]?.url
          ? `${process.env.NEXT_PUBLIC_STRAPI_URL}${json.logo.logo[0].url}`
          : null,
      } : null);

      // Specialties
      setSpecialties(json.specialties || []);

      // Navbar Links
      setNavLinks(json.navLinks.slice(0, -1) || []);
      setLocateUs(json.locateUs || null);
    } catch (err) {
      console.error("Error fetching navbar data:", err);
    }
  };
 
  fetchData();
 
}, [language]);

  // ---------------------------- MENU HANDLERS ----------------------------
  const toggleMobileMenu = () => setMobileMenuOpen(!mobileMenuOpen);
  const toggleDropdown = (id: number) =>
    setActiveDropdown(activeDropdown === id ? null : id);
  const closeAllMenus = () => {
    setMobileMenuOpen(false);
    setActiveDropdown(null);
  };

  const getNavItemClass = () =>
    "min-w-[80px] text-center px-3 py-2 rounded-md text-sm font-medium h-10 flex items-center justify-center transition-all duration-200 text-gray-700 dark:text-white hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800";

  const getButtonClass = () =>
    "flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium h-10 min-w-[80px] justify-center transition-all duration-200 text-gray-700 dark:text-white hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800";

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      className="fixed top-0 w-full z-50 bg-white/90 dark:bg-gray-900/90 backdrop-blur-md shadow-lg border-b border-gray-200/30 dark:border-gray-700/40"
      style={{ height: "64px" }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full">
        <div className="flex justify-between items-center h-full">
          {/* LOGO  
          <Link
            href="/"
            className="flex items-center space-x-2 group"
            onClick={closeAllMenus}
          >
            {logo?.url ? (
              <img
                src={logo.url}
                alt={logo.namelogo}
                className="w-8 h-8 object-contain transition-transform duration-300 group-hover:scale-110"
              />
            ) : (
              <div className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-white bg-gradient-to-r from-blue-600 to-purple-700">
                E
              </div>
            )}

            <div className="flex flex-col leading-tight text-left">
              <h1 className="font-bold text-lg text-gray-900 dark:text-white whitespace-nowrap">
                {logo?.namelogo || "ENAP"}
              </h1>
              <p className="text-[7px] font-medium tracking-wide whitespace-nowrap text-gray-600 dark:text-white/80">
                {logo?.Reference || "UNE REFERENCE NATIONALE"}
              </p>
            </div>
          </Link>
*/}
 {/* LOGO */}
<Link
  href="/"
  className="flex items-center space-x-2 group shrink-0 min-w-fit"
  onClick={closeAllMenus}
>
  {logo?.url ? (
    <img
      src={logo.url}
      alt={logo.namelogo}
      className="w-8 h-8 object-contain transition-transform duration-300 group-hover:scale-110 shrink-0"
    />
  ) : (
    <div className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-white bg-gradient-to-r from-blue-600 to-purple-700 shrink-0">
      E
    </div>
  )}

  <div className="flex flex-col leading-tight text-left shrink-0">
    <h1 className="font-bold text-lg text-gray-900 dark:text-white whitespace-nowrap">
      {logo?.namelogo || "ENAP"}
    </h1>
    <p className="text-[7px] font-medium tracking-wide whitespace-nowrap text-gray-600 dark:text-white/80">
      {logo?.Reference || "UNE REFERENCE NATIONALE"}
    </p>
  </div>
</Link>
          {/* DESKTOP MENU */}
          <div className="hidden lg:flex items-center space-x-2 h-full">
            <ul className="flex items-center space-x-1 h-full">
              {navLinks.map((link) => (
                <li key={link.id} className="relative h-full flex items-center">
                  {link.submenu && link.submenu.length > 0 ? (
                    <div className="relative h-full flex items-center">
                      <button onClick={() => toggleDropdown(link.id)} className={getButtonClass()}>
                        <span className="whitespace-nowrap text-sm">{link.label}</span>
                        <ChevronDown
                          className={`w-3 h-3 transition-transform duration-200 ${activeDropdown === link.id ? "rotate-180" : ""
                            }`}
                        />
                      </button>
                      {activeDropdown === link.id && (
                        <div className="absolute top-full left-0 mt-1 w-52 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/95 backdrop-blur-md z-50">
                          {link.submenu.map((subItem) => (
                            <Link
                              key={subItem.id}
                              href={subItem.url}
                              className="block px-3 py-2 text-sm transition-all duration-200 border-b border-gray-100 dark:border-gray-700 text-gray-700 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white"
                              onClick={() => setActiveDropdown(null)}
                            >
                              <span className="whitespace-nowrap text-sm">{subItem.label}</span>
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <Link href={link.url} className={getNavItemClass()}>
                      <span className="whitespace-nowrap text-sm">{link.label}</span>
                    </Link>
                  )}
                </li>
              ))}
            </ul>

            {/* Locate Us */}
            {locateUs && (
              <Link
                href={locateUs.url}
                className="flex items-center space-x-1 px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200 shadow-md hover:scale-105 bg-gradient-to-r from-[#00C7D9] via-[#0097B2] to-[#003F87] text-white min-h-[40px]"
              >
                <MapPin className="w-4 h-4" />
                <span className="whitespace-nowrap text-sm">{locateUs.label}</span>
              </Link>
            )}

            {/* LANG + THEME */}
            
            <div className="flex items-center space-x-2 h-full">
              {["en", "fr", "ar"].map((lang) => (
                <button
                  key={lang}
     onClick={() => {
            setLanguage(lang);
            router.push("/"); // ✅ redirect to Home
          }}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${language === lang
                      ? "bg-blue-600 text-white"
                      : "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-white hover:bg-gray-200 dark:hover:bg-gray-700"
                    }`}
                >
                  {lang.toUpperCase()}
                </button>
              ))}

              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg transition-all duration-200 hover:scale-110 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-white min-h-[40px] min-w-[40px] flex items-center justify-center"
              >
                {theme === "light" ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* MOBILE MENU BUTTON */}
          <div className="lg:hidden flex items-center space-x-2">
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg transition-all duration-200 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-white min-h-[40px] min-w-[40px] flex items-center justify-center"
            >
              {theme === "light" ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
            </button>

            <button
              onClick={toggleMobileMenu}
              className="p-2 rounded-lg transition-all duration-200 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-white min-h-[40px] min-w-[40px] flex items-center justify-center"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* MOBILE MENU */}
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden mobile-menu rounded-lg mt-1 shadow-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900/95 backdrop-blur-md absolute left-4 right-4"
          >
            <div className="p-4 space-y-2">
              {navLinks.map((link) => (
                <div key={link.id}>
                  {link.submenu && link.submenu.length > 0 ? (
                    <div>
                      <button
                        onClick={() => toggleDropdown(link.id)}
                        className="flex items-center justify-between w-full px-3 py-3 rounded-md text-sm font-medium transition-all duration-200 text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800 min-h-[44px]"
                      >
                        {link.label}
                        <ChevronDown
                          className={`w-3 h-3 transition-transform duration-200 ${activeDropdown === link.id ? "rotate-180" : ""
                            }`}
                        />
                      </button>
                      {activeDropdown === link.id && (
                        <div className="ml-3 mt-1 space-y-1">
                          {link.submenu.map((subItem) => (
                            <Link
                              key={subItem.id}
                              href={subItem.url}
                              className="block px-3 py-2.5 rounded text-sm text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200 min-h-[40px] flex items-center"
                              onClick={closeAllMenus}
                            >
                              {subItem.label}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <Link
                      href={link.url}
                      className="block px-3 py-3 rounded-md text-sm font-medium text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200 min-h-[44px] flex items-center"
                      onClick={closeAllMenus}
                    >
                      {link.label}
                    </Link>
                  )}
                </div>
              ))}

              {/* Locate Us */}
              {locateUs && (
                <Link
                  href={locateUs.url}
                  className="flex items-center justify-center space-x-1 px-3 py-3 rounded-md text-sm font-semibold bg-gradient-to-r from-blue-600 to-purple-600 text-white transition-all duration-200 min-h-[44px]"
                  onClick={closeAllMenus}
                >
                  <MapPin className="w-4 h-4" />
                  {locateUs.label}
                </Link>
              )}

              {/* Language Switcher */}
              <div className="p-3 rounded-md bg-gray-50 dark:bg-gray-800/50">
                <p className="text-xs font-medium mb-2 text-gray-600 dark:text-white/70">
                  Select Language:
                </p>
                <div className="flex gap-3">
     {["en", "fr", "ar"].map((lang) => (
  <button
    key={lang}
    onClick={() => setLanguage(lang)} // juste mettre à jour la langue
    className={`px-3 py-1 rounded-md text-sm font-medium transition ${
      language === lang
        ? "bg-blue-600 text-white"
        : "bg-gray-300 text-gray-800 hover:bg-gray-400 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
    }`}
  >
    {lang.toUpperCase()}
  </button>
))}

                </div>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </motion.nav>
  );
}




















 